import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  useLocation,
} from "react-router";
import { MsalProvider } from "@azure/msal-react";
import { msalApp } from "./Auth/msalApp";
import { AuthorizationProvider } from "./Auth/AuthorizationProvider";
import { UserPreferencesProvider } from "./Contexts/UserPreferencesContext";
import { Card, Image } from "react-bootstrap";
import { center, formStyle, msStudiosLogoFooter } from "./Constants/styles";
import version from "./version.json";
import logoImage from "./Images/microsoft_studios.png";
import "./index.css";
import { UserRole } from "./Models/User";
import { useIsMobile } from "./Utils/useIsMobile";
import { DebugOverlay } from "./Utils/DebugOverlay";
import { InstallAppHint } from "./Components/InstallAppHint";
import { ManifestLink } from "./Components/ManifestLink";

import type { Route } from "./+types/root";

export const links: Route.LinksFunction = () => [
  { rel: "icon", href: "/favicon.png" },
  { rel: "apple-touch-icon", href: "/icons/apple-touch-icon.png" },
  { rel: "stylesheet", href: "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css", crossOrigin: "anonymous" },
  { rel: "stylesheet", href: "https://cdn.jsdelivr.net/npm/bootswatch@5.3.2/dist/cerulean/bootstrap.min.css", crossOrigin: "anonymous" },
  { rel: "stylesheet", href: "https://unpkg.com/react-table@6.10.3/react-table.css" },
  { rel: "stylesheet", href: "https://fonts.googleapis.com/css?family=Noto+Sans:400,700" },
];

function isExternal() {
  if (typeof window === "undefined") return false;
  const role = localStorage.getItem("role");
  return role !== UserRole.Administrator && role !== UserRole.ContentManager;
}

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="description" content="Microsoft Flight Simulator Marketplace Content Portal (MCP)" />
        <meta name="theme-color" content="#1a1a1a" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        <meta name="apple-mobile-web-app-title" content="MCP" />
        <title>Marketplace Content Portal</title>
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function Root() {
  // Maintenance mode check (client-side only)
  if (typeof window !== "undefined" && import.meta.env.VITE_OFFLINE_FOR_MAINTENANCE === "true" && isExternal()) {
    const message = import.meta.env.VITE_OFFLINE_MESSAGE;
    return (
      <main className="container">
        <div className="row">
          <Card style={formStyle} className="col-xs-12 col-sm-12 col-md-12 col-lg-12">
            <div style={center}>
              <h4>{message}</h4>
            </div>
          </Card>
        </div>
      </main>
    );
  }

  return (
    <MsalProvider instance={msalApp}>
      <AuthorizationProvider>
        <UserPreferencesProvider>
        <RootContent />
        </UserPreferencesProvider>
      </AuthorizationProvider>
    </MsalProvider>
  );
}

function RootContent() {
  const location = useLocation();
  const isPopout = location.pathname === '/chat-popout';
  const isMobile = useIsMobile();

  return (
    <div style={isMobile ? { display: 'flex', flexDirection: 'column', minHeight: '100dvh' } : { display: 'flex', flexDirection: 'column', height: '100dvh', maxHeight: '100dvh', overflow: 'hidden' }}>
      <ManifestLink />
      <Outlet />
      {!isPopout && <InstallAppHint />}
      {!isPopout && (
        <footer style={isMobile ? { position: 'fixed', bottom: 0, left: 0, right: 0, display: 'flex', alignItems: 'center', color: 'white', padding: '6px 10px calc(12px + env(safe-area-inset-bottom, 0px))', zIndex: 1030, backgroundColor: '#1a1a1a' } : { display: 'flex', alignItems: 'center', flexShrink: 0, color: 'white', padding: '1.5rem' }}>
          <Image src={logoImage} style={{ ...msStudiosLogoFooter, height: isMobile ? '56px' : '75px', flexShrink: 0, marginLeft: 0, marginRight: 0 }} thumbnail />
          <span style={{ position: 'absolute', left: 0, right: 0, textAlign: 'center', pointerEvents: 'none', fontSize: isMobile ? '11px' : undefined }}>
            <span style={{ pointerEvents: 'auto' }}>
              Microsoft &copy; 2018 - 2026 {isMobile ? <br /> : '| Xbox Game Studios | '}
              MSFS Partner Use Only {isMobile ? <br /> : '| '}
              {version.version} {isMobile && '| '}<a style={{ color: "white" }} href="/attributions.txt" download>Attributions</a>
            </span>
          </span>
        </footer>
      )}
      <DebugOverlay />
    </div>
  );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "Oops!";
  let details = "An unexpected error occurred.";
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error";
    details = error.status === 404
      ? "The requested page could not be found."
      : error.statusText || details;
  } else if (import.meta.env.DEV && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return (
    <main className="container">
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && <pre><code>{stack}</code></pre>}
    </main>
  );
}
